/**
 * The search slot's lookup (S6). useCustomerSearch's pattern: nothing below two characters, a
 * 250 ms debounce, and a sequence guard so a slow earlier answer never replaces a newer one.
 *
 *   providers  the module registry's search providers, in registry order (G0; Quotes first —
 *              its provider reads the S1a list contract, so a quote is found exactly as the
 *              list finds it). Each answers with its own rows, or fails on its own
 *   others     POST /api/search as the header search sent it before S6, for the entities no
 *              module provides (products, customers, notes); they land where they landed
 *
 * Each part fails on its own: a provider's error still shows the other groups, and the reverse.
 */
import { useEffect, useRef, useState } from 'react';
import apiClient from '../../../services/apiClient';
import { searchProviders } from '../../../modules/registry';

export { SLOT_QUOTE_LIMIT, slotQuotesUrl } from '../../quotes/quoteSearchProvider';

export const SLOT_MIN_CHARS = 2;
export const SLOT_DEBOUNCE_MS = 250;
/** /api/search's entity keys, quotations left out: quotes come from the Quotes provider. */
export const SLOT_OTHER_ENTITIES = ['products', 'customers', 'customer_notes'];

const IDLE = Object.freeze({ term: '', groups: {}, errors: {}, others: [], loading: false, othersError: null });

export default function useSlotSearch(term, providers = searchProviders()) {
  const [state, setState] = useState(IDLE);
  const seqRef = useRef(0);
  const providersRef = useRef(providers);
  providersRef.current = providers;

  useEffect(() => {
    const trimmed = (term || '').trim();
    seqRef.current += 1;
    const seq = seqRef.current;

    if (trimmed.length < SLOT_MIN_CHARS) {
      setState(IDLE);
      return undefined;
    }

    setState((prev) => ({ ...prev, loading: true }));

    const timer = setTimeout(async () => {
      const list = providersRef.current;
      // The providers are asked first, then /api/search: the order S6 sent them in.
      const settled = await Promise.allSettled([
        ...list.map((p) => p.search(trimmed)),
        apiClient.post('/api/search', {
          query: trimmed,
          surface: 'quotation',
          limit: 20,
          entities: SLOT_OTHER_ENTITIES
        })
      ]);
      const answers = settled.slice(0, list.length);
      const others = settled[list.length];
      if (seq !== seqRef.current) return;
      const groups = {};
      const errors = {};
      list.forEach((p, i) => {
        const a = answers[i];
        groups[p.id] = a.status === 'fulfilled' && Array.isArray(a.value) ? a.value : [];
        if (a.status === 'rejected') errors[p.id] = p.errorText || `${p.label} search unavailable`;
      });
      setState({
        term: trimmed,
        groups,
        errors,
        others: others.status === 'fulfilled'
          ? (others.value?.data?.data?.results || []).filter((r) => r.entity_type !== 'quotation')
          : [],
        loading: false,
        othersError: others.status === 'rejected' ? 'Search unavailable' : null
      });
    }, SLOT_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [term]);

  return state;
}
